import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Button } from '@/components/ui/Button';
import { shortId } from '@/lib/format';
import { container } from '@/services/container';
import { parseQrContact, type QrContact } from '@/lib/qrContact';
import { parseQrPay, type QrPay } from '@/lib/qrPay';

export type QrScanMode = 'idle' | 'camera' | 'upload' | 'result' | 'error' | 'pay';

export interface QrScannerSheetProps {
  /** 本人 peerId（用于拦截自扫好友码） */
  myPeerId: string;
  /** 确认添加好友回调；仅好友码时触发。父组件调用 addContact，返回 true 表示成功（成功后本组件复位） */
  onAdd?: (c: QrContact) => Promise<boolean>;
  busy?: boolean;
  /** 确认付款回调；仅收款码（meshlink://pay）时触发。父组件调用 wallet.transfer，返回 true 表示成功 */
  onPay?: (to: string, amountDpb: string) => Promise<boolean>;
  payBusy?: boolean;
}

/**
 * 扫一扫：摄像头 / 上传图片 → jsqr 解码 → 好友确认卡 或 付款确认卡。
 * 只负责「识别 + 确认」：好友码走 onAdd，收款码（meshlink://pay）走 onPay。
 */
export function QrScannerSheet({
  myPeerId,
  onAdd,
  busy = false,
  onPay,
  payBusy = false,
}: QrScannerSheetProps): JSX.Element {
  const [mode, setMode] = useState<QrScanMode>('idle');
  const [contact, setContact] = useState<QrContact | null>(null);
  const [pay, setPay] = useState<QrPay | null>(null);
  const [error, setError] = useState('');
  /** v3.31 商家抵扣护栏：链上治理参数（meshStats.params 缓存） */
  const [guard, setGuard] = useState<Record<string, number>>({});
  /** 商家收银台：顾客消费金额（法币/元） */
  const [cash, setCash] = useState('');
  /** v3.31b 付款失败面板内提示（持久展示，替代一闪而过的 toast） */
  const [payErr, setPayErr] = useState('');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const modeRef = useRef<QrScanMode>('idle');

  useEffect(() => {
    void container.chain
      .meshStats()
      .then((st) => setGuard(((st?.params ?? {}) as Record<string, number>) ?? {}))
      .catch(() => undefined);
  }, []);

  const stopCamera = (): void => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
  };

  useEffect(() => {
    modeRef.current = mode;
    if (mode === 'idle' || mode === 'result' || mode === 'error') stopCamera();
    if (mode === 'error') {
      const t = window.setTimeout(() => setMode('idle'), 3000);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [mode]);

  useEffect(() => stopCamera, []);

  /** 解码文本分流：好友码 → result；收款码 → pay；否则返回 null（继续扫）。 */
  const routeCode = (text: string): boolean => {
    const c = parseQrContact(text);
    if (c) {
      if (c.peerId === myPeerId) {
        setError('这是你自己的二维码，不能添加自己为好友');
        setMode('error');
        return true;
      }
      setContact(c);
      setMode('result');
      return true;
    }
    const p = parseQrPay(text);
    if (p) {
      if (p.to === myPeerId.toLowerCase()) {
        // 链地址 ≠ peerId，通常不会命中；此处仅防御
      }
      setPay(p);
      setMode('pay');
      return true;
    }
    return false;
  };

  /** 摄像头循环解码：每帧从 video 截帧 → jsqr。命中合法码即停。 */
  const startCamera = async (): Promise<void> => {
    setError('');
    setMode('camera');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 } },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) throw new Error('no video');
      video.srcObject = stream;
      await video.play();
    } catch {
      setError('无法访问摄像头（可能被拒绝或无摄像头），请改用「从图片识别」');
      setMode('error');
      return;
    }

    const tick = (): void => {
      if (modeRef.current !== 'camera') return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState >= 2 && video.videoWidth > 0) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          try {
            const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(img.data, img.width, img.height);
            if (code?.data && routeCode(code.data)) {
              stopCamera();
              return;
            }
          } catch {
            /* 单帧解码异常忽略，继续 */
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  /** 上传图片单次解码。 */
  const scanFile = async (file: File): Promise<void> => {
    setError('');
    setMode('upload');
    try {
      const bmp = await createImageBitmap(file);
      const canvas = canvasRef.current;
      if (!canvas) return;
      // 大图缩放至 ≤1024，保证 jsqr 解码速度与成功率
      const maxDim = 1024;
      const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
      canvas.width = Math.max(1, Math.round(bmp.width * scale));
      canvas.height = Math.max(1, Math.round(bmp.height * scale));
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(img.data, img.width, img.height);
      if (code?.data && routeCode(code.data)) return;
      setError('未识别到有效的 MeshLink 二维码（好友码 meshlink://add 或 收款码 meshlink://pay）');
      setMode('error');
    } catch {
      setError('图片读取失败，请重试或换一张');
      setMode('error');
    }
  };

  const reset = (): void => {
    stopCamera();
    setContact(null);
    setPay(null);
    setCash('');
    setPayErr('');
    setError('');
    setMode('idle');
  };

  return (
    <div className="flex flex-col items-stretch gap-3">
      {/* 隐藏解码画布 */}
      <canvas ref={canvasRef} className="hidden" />

      {mode === 'idle' ? (
        <>
          <Button block onClick={() => void startCamera()}>摄像头扫码</Button>
          <Button block variant="outline" onClick={() => fileRef.current?.click()}>从图片识别</Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void scanFile(f);
              e.target.value = '';
            }}
          />
          <p className="text-center text-xs text-dim">对准好友二维码或 DPB 收款码，即可添加好友 / 付款</p>
        </>
      ) : null}

      {mode === 'camera' || mode === 'upload' ? (
        <div className="flex flex-col items-center gap-3">
          {mode === 'camera' ? (
            <div className="relative w-full overflow-hidden rounded-card border border-line" style={{ aspectRatio: '4/3' }}>
              <video ref={videoRef} playsInline muted className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-40 w-40 rounded-lg border-2 border-dashed border-accent/70" />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center rounded-card border border-line py-10">
              <p className="text-sm text-dim">正在识别图片…</p>
            </div>
          )}
          <Button size="sm" variant="ghost" onClick={reset}>取消</Button>
        </div>
      ) : null}

      {mode === 'result' && contact ? (
        <div className="flex flex-col items-center gap-3">
          <div className="w-full rounded-card border border-line bg-panel-2/60 p-4 text-center">
            <p className="text-base font-medium text-text">{contact.name || `好友_${contact.peerId.slice(-4)}`}</p>
            {contact.did ? (
              <p className="mono mt-1 break-all text-[11px] text-dim">{shortId(contact.did, 14, 6)}</p>
            ) : null}
            <p className="mono mt-2 break-all text-[11px] text-dim">{shortId(contact.peerId, 10, 6)}</p>
            <p className="mt-2 text-xs text-dim">添加后双方将交换加密密钥，开始端到端加密聊天</p>
          </div>
          <div className="flex w-full gap-2">
            <Button block variant="ghost" onClick={reset}>重新扫码</Button>
            <Button
              block
              loading={busy}
              disabled={!onAdd}
              onClick={() => {
                if (!onAdd) return;
                void onAdd(contact).then((ok) => {
                  if (ok) reset();
                });
              }}
            >
              添加好友
            </Button>
          </div>
        </div>
      ) : null}

      {mode === 'pay' && pay ? (
        <div className="flex flex-col items-center gap-3">
          <div className="w-full rounded-card border border-line bg-panel-2/60 p-4 text-center">
            {pay.deduct ? (
              <>
                <p className="text-sm font-medium text-text">商家 DPB 抵扣收银台</p>
                <p className="mono mt-2 break-all text-[11px] text-dim">{pay.to}</p>
                <label className="mt-3 block space-y-1 text-left">
                  <span className="text-xs text-dim">消费金额（元）</span>
                  <input
                    className="w-full rounded-btn border border-line bg-panel-2 px-3 py-2 text-sm text-text outline-none focus:border-accent"
                    type="number"
                    min="0"
                    step="any"
                    value={cash}
                    onChange={(e) => setCash(e.target.value)}
                    placeholder="输入消费金额"
                  />
                </label>
                {(() => {
                  const rate = Math.max(0, Math.min(1, Number(pay.deduct ?? 0)));
                  const cashNum = Number(cash);
                  const raw = Number.isFinite(cashNum) && cashNum > 0 ? cashNum * rate : 0;
                  const cap = Number(guard.user_deduct_single_cap ?? 200);
                  const dpb = Math.min(raw, cap);
                  const fiat = Number.isFinite(cashNum) && cashNum > 0 ? Math.max(0, cashNum - dpb) : 0;
                  const subsidy = Number(guard.platform_subsidy_ratio ?? 0);
                  return (
                    <>
                      <div className="mt-3 rounded-btn bg-panel-2 px-3 py-2">
                        <div className="text-[11px] text-dim">DPB 抵扣（{(rate * 100).toFixed(1)}%）</div>
                        <div className="text-lg font-semibold text-ok">{dpb.toFixed(4)} DPB</div>
                        <div className="text-[11px] text-dim">实付法币 ¥{fiat.toFixed(2)}</div>
                      </div>
                      <p className="mt-2 text-[11px] leading-relaxed text-dim">
                        DPB 抵扣部分将签名上链转给商家；法币部分线下/聚合支付结算。
                        {subsidy > 0
                          ? `平台按抵扣额补贴商家 ${(subsidy * 100).toFixed(0)}% 权益。`
                          : '平台补贴已关闭（成熟期）。'}
                        抵扣按消费权益口径计（1 DPB ≈ 1 元抵扣，非兑换）。
                      </p>
                    </>
                  );
                })()}
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-text">向以下地址付款（DPB）</p>
                <p className="mono mt-2 break-all text-[11px] text-dim">{pay.to}</p>
                <p className="mt-3 text-lg font-semibold text-ok">
                  {pay.amount ? `${pay.amount} DPB` : '任意金额'}
                </p>
                <p className="mt-2 text-xs text-dim">将从你的 MeshLink 钱包余额中扣除并签名上链</p>
              </>
            )}
          </div>
          <div className="flex w-full gap-2">
            <Button block variant="ghost" onClick={reset}>重新扫码</Button>
            <Button
              block
              loading={payBusy}
              disabled={!onPay}
              onClick={() => {
                if (!onPay) return;
                const amount = pay.deduct
                  ? (() => {
                      const rate = Math.max(0, Math.min(1, Number(pay.deduct ?? 0)));
                      const cashNum = Number(cash);
                      const raw = Number.isFinite(cashNum) && cashNum > 0 ? cashNum * rate : 0;
                      const cap = Number(guard.user_deduct_single_cap ?? 200);
                      return String(Math.min(raw, cap));
                    })()
                  : (pay.amount ?? '');
                setPayErr('');
                if (amount === '0') {
                  setPayErr(pay.deduct ? '请输入有效的消费金额' : '请输入有效的付款金额');
                  return;
                }
                void onPay(pay.to, amount).then((ok) => {
                  if (ok) {
                    reset();
                  } else {
                    setPayErr('付款失败：余额不足或链端拒绝（如不能转给自己），请检查后重试');
                  }
                });
              }}
            >
              确认付款
            </Button>
          </div>
          {payErr ? (
            <p className="w-full rounded-btn border border-err/40 bg-err/10 px-3 py-2 text-center text-xs text-err">
              {payErr}
            </p>
          ) : null}
        </div>
      ) : null}

      {mode === 'error' ? (
        <div className="flex flex-col items-center gap-3">
          <p className="text-center text-sm text-err">{error}</p>
          <Button size="sm" variant="ghost" onClick={reset}>返回</Button>
        </div>
      ) : null}
    </div>
  );
}

export default QrScannerSheet;
